import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAstroCommissionHistory,
  getAstrologersForDropdown,
} from "../../services/astroCommissionHistoryService";
import { toast } from "react-toastify";

// API Response shape:
// {
//   success: true,
//   message: "...",
//   summary: { totalCommission: 0.8 },
//   data: [ ...records ],
//   pagination: { total, page, limit, totalPages }
// }

const initialState = {
  records:     [],
  total:       0,
  page:        1,
  totalPages:  1,
  summary: {
    totalCommission: 0,
  },
  status: "idle",
  error:  null,

  // dropdown
  astrologers:       [],
  astrologersStatus: "idle",
};

// ── Thunks ────────────────────────────────────────────────────────────────────

export const fetchAstroCommissionHistory = createAsyncThunk(
  "astroCommissionHistory/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      return await getAstroCommissionHistory(params);
    } catch (error) {
      const msg =
        error.response?.data?.message || "Failed to fetch commission history";
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

export const fetchAstrologersDropdown = createAsyncThunk(
  "astroCommissionHistory/fetchAstrologers",
  async (_, { rejectWithValue }) => {
    try {
      return await getAstrologersForDropdown();
    } catch (error) {
      return rejectWithValue("Failed to fetch astrologers");
    }
  }
);

// ── Slice ─────────────────────────────────────────────────────────────────────

const astroCommissionHistorySlice = createSlice({
  name: "astroCommissionHistory",
  initialState,
  reducers: {
    clearError: (state) => { state.error = null; },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // fetchAstroCommissionHistory
      .addCase(fetchAstroCommissionHistory.pending, (state) => {
        state.status = "loading";
        state.error  = null;
      })
      .addCase(fetchAstroCommissionHistory.fulfilled, (state, action) => {
        const payload    = action.payload;
        state.status     = "succeeded";
        state.records    = payload.data || [];
        state.summary    = payload.summary || { totalCommission: 0 };
        // ✅ reads from pagination object — same pattern as payment link history
        state.total      = payload.pagination?.total      || 0;
        state.page       = payload.pagination?.page       || 1;
        state.totalPages = payload.pagination?.totalPages || 1;
        state.error      = null;
      })
      .addCase(fetchAstroCommissionHistory.rejected, (state, action) => {
        state.status  = "failed";
        state.error   = action.payload;
        state.records = [];
        state.total   = 0;
        state.page    = 1;
      })

      // fetchAstrologersDropdown
      .addCase(fetchAstrologersDropdown.pending, (state) => {
        state.astrologersStatus = "loading";
      })
      .addCase(fetchAstrologersDropdown.fulfilled, (state, action) => {
        state.astrologersStatus = "succeeded";
        const payload           = action.payload;
        state.astrologers =
          payload.data?.astrologers ||
          payload.data?.data        ||
          payload.data              ||
          [];
      })
      .addCase(fetchAstrologersDropdown.rejected, (state) => {
        state.astrologersStatus = "failed";
        state.astrologers       = [];
      });
  },
});

export const { clearError, resetState } = astroCommissionHistorySlice.actions;

// ── Selectors ─────────────────────────────────────────────────────────────────
export const selectCommissionRecords  = (s) => s.astroCommissionHistory.records;
export const selectCommissionTotal    = (s) => s.astroCommissionHistory.total;
export const selectCommissionPage     = (s) => s.astroCommissionHistory.page;
export const selectCommissionSummary  = (s) => s.astroCommissionHistory.summary;
export const selectCommissionStatus   = (s) => s.astroCommissionHistory.status;
export const selectAstrologersDropdown = (s) => s.astroCommissionHistory.astrologers;

export default astroCommissionHistorySlice.reducer;